import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PaymentGatewayType } from 'components/payment/payment.constants';
import { usePaymentGatewayMatches } from 'components/payment/payment.new.hooks';
import { selectPaymentInstrumentById } from 'redux/user/user.selectors';
import {
  useSquareTokenizeStoredCard,
  useSquareVerificationDetails,
} from 'components/payment/square/square-card/square-card.hooks';

/**
 * Buying or reloading a gift card from My Cards charges a card the shopper
 * already saved. Square treats that as customer initiated, so the stored ccof:
 * id carries no verification and cannot be charged on its own - it has to be
 * re-tokenized with SCA first.
 *
 * Returns a function that resolves to { token, ok }:
 *   ok false  - the shopper did not finish the challenge, make no purchase
 *   token     - the verified token to charge, or undefined on gateways that
 *               need no verification, where the thunk keeps using the stored id
 *
 * @param {string} payingCardId paymentInstrumentId of the card being charged
 */
export const useVerifyGiftCardPayingCard = payingCardId => {
  const matchesPaymentGateway = usePaymentGatewayMatches();
  const isSquare = matchesPaymentGateway([PaymentGatewayType.SQUARE]);

  const payingCardSelector = useCallback(
    state => selectPaymentInstrumentById(payingCardId)(state),
    [payingCardId],
  );
  const payingCard = useSelector(payingCardSelector);

  const tokenizeStoredCard = useSquareTokenizeStoredCard();
  const buildVerificationDetails = useSquareVerificationDetails();

  return useCallback(
    async amountInMilliunits => {
      if (!isSquare) return { token: undefined, ok: true };

      const token = await tokenizeStoredCard(
        buildVerificationDetails({ intent: 'CHARGE', amountInMilliunits }),
        payingCard?.paymentToken,
      );

      return { token, ok: !!token };
    },
    [isSquare, tokenizeStoredCard, buildVerificationDetails, payingCard],
  );
};
